import { useMemo, useState } from "react";
import { api } from "../api";
import type { CaseRecord } from "../types";
import {
  RECORD_CATEGORIES,
  categoryMeta,
  effectiveCategory,
  type CategoryId,
} from "../lib/recordCategory";

// The document-kind category UI: a per-row badge that is ALSO the override
// control, a filter bar over the Records list, and the opt-in AI-sort review.
// All assignments (deterministic, manual, or confirmed AI) are overridable and
// the override persists server-side; AI suggestions are never applied silently.

const AUTO = "__auto__";

// A record's category shown as an editable chip. Changing it writes a user
// override (wins over the deterministic auto value); "↺ Auto" clears it.
export function CategoryBadge({
  record,
  caseId,
  onUpdated,
}: {
  record: CaseRecord;
  caseId: string;
  onUpdated: (r: CaseRecord) => void;
}) {
  const eff = effectiveCategory(record);
  const meta = categoryMeta(eff);
  const overridden = !!record.category_override;
  const autoMeta = categoryMeta(record.record_category || "other");

  async function change(v: string) {
    try {
      if (v === AUTO) {
        await api.clearRecordCategory(caseId, record.id);
        onUpdated({ ...record, category_override: null });
      } else if (v !== eff) {
        await api.setRecordCategory(caseId, record.id, v);
        onUpdated({ ...record, category_override: v });
      }
    } catch (err) {
      window.alert(`Couldn't update category: ${(err as Error).message}`);
    }
  }

  return (
    <select
      className={`cat-badge cat-${eff}${overridden ? " cat-overridden" : ""}`}
      value={eff}
      onChange={(e) => void change(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      title={
        overridden
          ? `You set this to ${meta.label} (auto: ${autoMeta.label})`
          : `${meta.label} — click to recategorize`
      }
      aria-label="Document category"
    >
      {overridden && <option value={AUTO}>↺ Auto ({autoMeta.label})</option>}
      {RECORD_CATEGORIES.map((c) => (
        <option key={c.id} value={c.id}>
          {c.emoji} {c.label}
        </option>
      ))}
    </select>
  );
}

// Filter bar + AI-sort. Chips for each non-empty bucket with counts; the active
// chip filters the Records list. When any record is uncategorized ('other'),
// an "Auto-sort" button runs the batched AI classifier and opens a review the
// user confirms per-record.
export function CategoryBar({
  records,
  value,
  onChange,
  caseId,
  onRecordUpdated,
}: {
  records: CaseRecord[];
  value: CategoryId | "all";
  onChange: (v: CategoryId | "all") => void;
  caseId: string;
  onRecordUpdated: (r: CaseRecord) => void;
}) {
  const counts = useMemo(() => {
    const m = new Map<CategoryId, number>();
    for (const r of records) {
      const c = effectiveCategory(r);
      m.set(c, (m.get(c) ?? 0) + 1);
    }
    return m;
  }, [records]);

  const byId = useMemo(
    () => new Map(records.map((r) => [r.id, r])),
    [records],
  );
  const otherIds = useMemo(
    () => records.filter((r) => effectiveCategory(r) === "other").map((r) => r.id),
    [records],
  );

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // AI suggestions pending the user's confirmation (bucket !== 'other' only).
  const [pending, setPending] = useState<{ id: string; bucket: CategoryId }[] | null>(null);

  async function autoSort() {
    setBusy(true);
    setError(null);
    try {
      const { suggestions } = await api.classifyRecords(caseId, otherIds);
      const useful = suggestions.filter(
        (s): s is { id: string; bucket: CategoryId } =>
          s.bucket !== "other" && categoryMeta(s.bucket).id === s.bucket,
      );
      setPending(useful);
      if (useful.length === 0) setError("AI didn't find a clearer category for these.");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm(s: { id: string; bucket: CategoryId }) {
    const rec = byId.get(s.id);
    if (!rec) return;
    try {
      await api.setRecordCategory(caseId, s.id, s.bucket);
      onRecordUpdated({ ...rec, category_override: s.bucket });
      setPending((prev) => prev?.filter((p) => p.id !== s.id) ?? null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function confirmAll() {
    if (!pending) return;
    for (const s of [...pending]) await confirm(s);
  }

  const chip = (id: CategoryId | "all", label: string, n: number) => (
    <button
      key={id}
      type="button"
      className={`cat-filter-chip${value === id ? " active" : ""}`}
      onClick={() => onChange(id)}
      aria-pressed={value === id}
    >
      {label} <span className="cat-filter-count">{n}</span>
    </button>
  );

  return (
    <div className="cat-bar">
      <div className="cat-filter-chips" role="group" aria-label="Filter by document category">
        {chip("all", "All", records.length)}
        {RECORD_CATEGORIES.filter((c) => (counts.get(c.id) ?? 0) > 0).map((c) =>
          chip(c.id, `${c.emoji} ${c.label}`, counts.get(c.id) ?? 0),
        )}
        {otherIds.length > 0 && (
          <button
            type="button"
            className="cat-autosort-btn"
            onClick={() => void autoSort()}
            disabled={busy}
            title="Use AI to suggest categories for the uncategorized records — you confirm each"
          >
            {busy ? "Sorting…" : `✨ Auto-sort Other (${otherIds.length})`}
          </button>
        )}
      </div>

      {error && <div className="cat-bar-error" role="status">{error}</div>}

      {pending && pending.length > 0 && (
        <div className="cat-review" role="region" aria-label="AI category suggestions">
          <div className="cat-review-head">
            <strong>AI suggestions</strong> — confirm to apply ({pending.length})
            <div className="cat-review-actions">
              <button type="button" className="btn-secondary" onClick={() => void confirmAll()}>
                Confirm all
              </button>
              <button type="button" className="btn-ghost" onClick={() => setPending(null)}>
                Dismiss
              </button>
            </div>
          </div>
          <ul className="cat-review-list">
            {pending.map((s) => {
              const rec = byId.get(s.id);
              const meta = categoryMeta(s.bucket);
              return (
                <li key={s.id} className="cat-review-row">
                  <span className="cat-review-label" title={rec?.subject ?? undefined}>
                    {rec?.content_label || rec?.subject || "(untitled)"}
                  </span>
                  <span className={`cat-suggestion cat-${s.bucket}`}>
                    {meta.emoji} {meta.label}?
                  </span>
                  <button type="button" className="cat-confirm" onClick={() => void confirm(s)}>
                    ✓ Confirm
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
